/*
SelectionPanel is a full screen panel with a title, cancel button, accept button and optional "clear all" button.
It renders its children below the title and places the Accept button at the bottom.
*/
import { JSX, PropsWithChildren } from "react"

import XMarkIcon from "@heroicons/react/20/solid/XMarkIcon"
import { twMerge } from "tailwind-merge"

import { Bee } from "~/src/components/BeeKit"

export type SelectionPanelProps = {
  title: string
  renderAction?: (props: ActionProps) => JSX.Element
  className?: string
  onClose?: () => void
  onClear?: () => void
  onApply?: () => void
  disabled?: boolean
}

type ActionProps = {
  onApply?: () => void
  onClose?: () => void
  disabled?: boolean
}

export function SelectionPanel(props: PropsWithChildren<SelectionPanelProps>) {
  const { title, renderAction, className, onClear, onClose, onApply, disabled = false, children } = props
  const RenderAction =
    renderAction ??
    (({ onApply, onClose, disabled }) => (
      <Bee.Button
        type="button"
        onClick={() => {
          onApply?.()
          onClose?.()
        }}
        className="w-full cursor-pointer disabled:cursor-not-allowed"
        disabled={disabled}
      >
        Apply
      </Bee.Button>
    ))

  return (
    <div className={twMerge("fixed inset-0 z-50 flex flex-col overflow-y-auto bg-white", className)}>
      <div className="flex justify-between px-4 py-3.5">
        <div className="flex gap-3">
          <h1 className="text-lg font-bold">{title}</h1>
          {onClear ? (
            <button type="button" className="text-xs text-gray-500 hover:cursor-pointer" onClick={onClear}>
              Clear all
            </button>
          ) : null}
        </div>
        <XMarkIcon onClick={onClose} className="ml-1 h-6 w-6 text-gray-600 hover:cursor-pointer" />
      </div>
      <div className="grow border-t p-4">{children}</div>
      <div className="px-6 pb-4">
        <RenderAction onClose={onClose} onApply={onApply} disabled={disabled} />
      </div>
    </div>
  )
}
